import _ from 'lodash'
import {
    ConfigObject,
    ConfigPath,
    ConfigValue,
    convertStateToDiff,
    getValueFromBase,
    getValueFromBaseIfNotInDiff
} from '../../../../../utils/deviceConfig'
import { Resolution } from './display.enums'
import { DisplayBasicConfigState } from './DisplayBasicConfig'

// Define types for the display configuration sections
export interface DeviceGeneralSection {
    lot_id: ConfigValue
    test_mode: ConfigValue
}

export interface ConnectionSection {
    lot_mqtt_ip: ConfigValue
}

export interface SystemSection {
    disable_gui_event_publish: ConfigValue
    gate_id: ConfigValue
    direction: ConfigValue
    min_confidence_level: ConfigValue
}

export interface MonitorSection {
    monitor_orientation: ConfigValue
    resolution_width: ConfigValue
    resolution_height: ConfigValue
    night_brightness: ConfigValue
    day_brightness: ConfigValue
}

export interface DisplayConfig extends DeviceGeneralSection, ConnectionSection, SystemSection, MonitorSection {}

// Define the paths for display configuration
export const PATHS_DISPLAY: Record<keyof DisplayConfig, ConfigPath> = {
    //General
    lot_id: 'global.generic_config.lot_id',
    test_mode: 'global.generic_config.test_mode',
    //Networking
    lot_mqtt_ip: 'mosquitto.lot_mqtt_ip',
    //Directions
    disable_gui_event_publish: 'backend.disable_gui_event_publish',
    gate_id: 'backend.gate_id',
    direction: 'global.generic_config.direction',
    min_confidence_level: 'backend.min_confidence_level',
    //Display
    monitor_orientation: 'gui.monitor_orientation',
    resolution_width: 'gui.resolution_width',
    resolution_height: 'gui.resolution_height',
    night_brightness: 'gui.night_brightness',
    day_brightness: 'gui.day_brightness'
}

/**
 * Gets the initial complete display configuration
 * @param baseConfig - Base configuration
 * @param diffConfig - Differential configuration
 * @returns Complete display configuration
 */
export const getInitialDisplayConfig = (baseConfig: ConfigObject, diffConfig: ConfigObject): DisplayConfig => {
    const baseConfigClone = _.cloneDeep(baseConfig)
    const diffConfigClone = _.cloneDeep(diffConfig)

    const result: Partial<DisplayConfig> = {}

    Object.entries(PATHS_DISPLAY).forEach(([key, path]) => {
        result[key as keyof DisplayConfig] = getValueFromBaseIfNotInDiff(baseConfigClone, diffConfigClone, path)
    })

    return result as DisplayConfig
}

export const getDefaultDisplayDeviceGeneralSection = (base: ConfigObject): Partial<DisplayConfig> => {
    return {
        test_mode: getValueFromBase(base, PATHS_DISPLAY.test_mode) as Partial<DisplayConfig>,
        lot_id: getValueFromBase(base, PATHS_DISPLAY.lot_id) as Partial<DisplayConfig>
    }
}

export const getDefaultDisplayNetworkSection = (base: ConfigObject): Partial<DisplayConfig> => {
    return {
        lot_mqtt_ip: getValueFromBase(base, PATHS_DISPLAY.lot_mqtt_ip) as Partial<DisplayConfig>
    }
}

export const getDefaultDisplayDetectionSection = (base: ConfigObject): Partial<DisplayConfig> => {
    return {
        disable_gui_event_publish: getValueFromBase(
            base,
            PATHS_DISPLAY.disable_gui_event_publish
        ) as Partial<DisplayConfig>,
        gate_id: getValueFromBase(base, PATHS_DISPLAY.gate_id) as Partial<DisplayConfig>,
        direction: getValueFromBase(base, PATHS_DISPLAY.direction) as Partial<DisplayConfig>,
        min_confidence_level: getValueFromBase(base, PATHS_DISPLAY.min_confidence_level) as Partial<DisplayConfig>
    }
}

export const getDefaultDisplayDisplaySection = (base: ConfigObject): Partial<DisplayConfig> => {
    return {
        monitor_orientation: getValueFromBase(base, PATHS_DISPLAY.monitor_orientation) as Partial<DisplayConfig>,
        resolution_width: getValueFromBase(base, PATHS_DISPLAY.resolution_width) as Partial<DisplayConfig>,
        resolution_height: getValueFromBase(base, PATHS_DISPLAY.resolution_height) as Partial<DisplayConfig>,
        night_brightness: getValueFromBase(base, PATHS_DISPLAY.night_brightness) as Partial<DisplayConfig>,
        day_brightness: getValueFromBase(base, PATHS_DISPLAY.day_brightness) as Partial<DisplayConfig>
    }
}

export type DefaultDisplayConfigSection = 'generalSection' | 'networkSection' | 'detectionSection' | 'displaySection'
export const defaultDisplayConfigFunctions: Record<
    DefaultDisplayConfigSection,
    (base: ConfigObject) => Partial<DisplayConfig>
> = {
    generalSection: getDefaultDisplayDeviceGeneralSection,
    networkSection: getDefaultDisplayNetworkSection,
    detectionSection: getDefaultDisplayDetectionSection,
    displaySection: getDefaultDisplayDisplaySection
}

export const buildDisplayConfigDiffFromState = (
    state: DisplayBasicConfigState | null,
    base: ConfigObject,
    diff: ConfigObject
): ConfigObject => {
    if (!state) return {}
    return convertStateToDiff(PATHS_DISPLAY, state as unknown as Record<string, ConfigValue>, diff, base)
}

export const getResolutionLabel = (width: number, height: number): Resolution => {
    if (width === 1920 && height === 1080) return Resolution.FULL_HD
    if (width === 3840 && height === 2160) return Resolution.FOUR_K
    return Resolution.FULL_HD
}

export const getResolutionDimensions = (label: Resolution): { width: number; height: number } => {
    switch (label) {
        case Resolution.FULL_HD:
            return { width: 1920, height: 1080 }
        case Resolution.FOUR_K:
            return { width: 3840, height: 2160 }
    }
}
